import { useTranslation } from '@thoughtspot/i18n';
import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { hasModernFMPrivilege } from '../../../utils/feature-management-privilege';
import { ConfigPrePostFixStrings } from '../../settings/common';
import { ConfigCard, InputType, } from '../config-components';
import { DATE_BUCKET_BACKEND_DEFAULT, DATE_BUCKET_KEY, DEBOUNCE_DELAY, } from './constants';
const toBackendValue = (configName, value) => {
    if (configName === DATE_BUCKET_KEY && value === 'default') {
        return DATE_BUCKET_BACKEND_DEFAULT;
    }
    return value;
};
export const ConfigRow = ({ config, viewMode, isModernFMEnabled, onSave, onSaveOrgAware, onPreserveOverrideNeeded, }) => {
    const { t } = useTranslation();
    const [localValue, setLocalValue] = useState(config?.value ?? '');
    useEffect(() => {
        setLocalValue(config?.value ?? '');
    }, [config?.value]);
    const buildRawOptions = useCallback((value) => {
        const keys = config.controlsFlags ?? [config.configName];
        return keys.map(key => ({
            optionKey: key,
            optionValue: toBackendValue(key, value),
        }));
    }, [config.controlsFlags, config.configName]);
    const doSave = useCallback((value, previousValue) => {
        if (!config?.configName)
            return;
        const orgAwareEditable = config.orgAware &&
            isModernFMEnabled &&
            hasModernFMPrivilege() &&
            !config.controlsFlags;
        if (orgAwareEditable) {
            if (viewMode === 'all') {
                onPreserveOverrideNeeded(config.configName, value, t(`${config.configName}.${ConfigPrePostFixStrings.LABEL}`), previousValue, () => setLocalValue(previousValue));
            }
            else {
                onSaveOrgAware(config.configName, value, previousValue, () => setLocalValue(previousValue));
            }
        }
        else {
            onSave(buildRawOptions(value), viewMode);
        }
    }, [
        config,
        viewMode,
        isModernFMEnabled,
        onSave,
        onSaveOrgAware,
        onPreserveOverrideNeeded,
        buildRawOptions,
        t,
    ]);
    const doSaveRef = useRef(doSave);
    doSaveRef.current = doSave;
    const debouncedSave = useRef(_.debounce((value, prev) => doSaveRef.current(value, prev), DEBOUNCE_DELAY)).current;
    useEffect(() => () => debouncedSave.cancel(), [debouncedSave]);
    if (!config?.configName || !config.elementType)
        return null;
    const elementType = config.elementType.toLowerCase();
    const configLabel = t(`${config.configName}.${ConfigPrePostFixStrings.LABEL}`);
    if (elementType === InputType.Toggle) {
        return (React.createElement(ConfigCard, { inputType: InputType.Toggle, "data-testid": config.configName, title: configLabel, checked: localValue === 'true', name: config.configName, onChange: () => {
                const prev = localValue;
                const next = prev === 'true' ? 'false' : 'true';
                setLocalValue(next);
                doSave(next, prev);
            } }));
    }
    if (elementType === InputType.Select) {
        const rawOptions = config.elementConfig?.groups?.[0]?.options ?? [];
        const options = rawOptions.map(o => ({
            id: String(o),
            label: t(`${config.configName}.${o}.${ConfigPrePostFixStrings.LABEL}`),
        }));
        return (React.createElement(ConfigCard, { inputType: InputType.Select, "data-testid": config.configName, title: configLabel, value: localValue, options: options, onChange: (val) => {
                const prev = localValue;
                setLocalValue(val);
                doSave(val, prev);
            } }));
    }
    if (elementType === InputType.Input) {
        const subType = (config.elementConfig?.type ?? 'text');
        return (React.createElement(ConfigCard, { inputType: InputType.Input, type: subType, "data-testid": config.configName, title: configLabel, value: localValue, maxLength: config.validation?.maxLength, onChange: (val) => {
                const prev = localValue;
                setLocalValue(val);
                debouncedSave(val, prev);
            } }));
    }
    return null;
};
//# sourceMappingURL=config-row.js.map